import React from 'react';
import { getProgressionPercent, getProgressionStepTone, getUpcomingProgressionStep } from './inquiriesShared';
import './StageProgressPill.css';

const getStepId = (step) => String(step?.id || step?.key || step?.step_id || step?.stepKey || '').trim();

const getCurrentStepIndex = (progression, steps, percent) => {
  if (!Array.isArray(steps) || steps.length === 0) return -1;

  const currentStepId = getStepId(getUpcomingProgressionStep(progression)) || getStepId(progression?.current_step);
  if (currentStepId) {
    const matchedIndex = steps.findIndex((step) => getStepId(step) === currentStepId);
    if (matchedIndex >= 0) return matchedIndex;
  }

  let touchedIndex = -1;
  steps.forEach((step, index) => {
    if (['planned', 'in_progress', 'done', 'approved', 'not_approved'].includes(step?.status)) {
      touchedIndex = index;
    }
  });
  if (touchedIndex >= 0) return touchedIndex;
  if (percent >= 100) return steps.length - 1;
  if (percent > 0) return Math.max(0, Math.ceil((percent / 100) * steps.length) - 1);
  return -1;
};

const getStepStatusClassName = (step) => {
  const tone = getProgressionStepTone(step);
  if (tone === 'done') return 'is-done';
  if (tone === 'planned') return 'is-planned';
  if (tone === 'not-approved') return 'is-not-approved';
  if (['red', 'orange', 'blue', 'green'].includes(tone)) return `is-${tone}`;
  return 'is-todo';
};

const shouldShowCustomStatusProgression = (progression) => !(
  progression?.custom_status?.label
  && (
    progression.custom_status.showProgressionStatus === false
    || progression.custom_status.show_progression_status === false
  )
);

export const getStageProgressPillClassName = (baseClassName, stageClassName, progression) => {
  const percent = getProgressionPercent(progression);
  const showProgress = Boolean(progression?.configured && percent > 0 && shouldShowCustomStatusProgression(progression));
  return [
    baseClassName,
    'stage-progress-pill',
    showProgress ? 'has-progress' : '',
    stageClassName,
  ].filter(Boolean).join(' ');
};

export const StageProgressPillContent = ({ label, progression }) => {
  const percent = getProgressionPercent(progression);
  const steps = Array.isArray(progression?.steps) ? progression.steps : [];
  const showCustomProgression = shouldShowCustomStatusProgression(progression);
  const showProgress = Boolean(progression?.configured && percent > 0 && steps.length > 0 && showCustomProgression);
  const showDynamicLabel = Boolean(
    progression?.custom_status?.label
    && showCustomProgression
    && progression?.dynamic_stage_label
    && progression.dynamic_stage_label !== progression.custom_status.label,
  );
  const currentStepIndex = showProgress ? getCurrentStepIndex(progression, steps, percent) : -1;
  const currentStepNumber = currentStepIndex >= 0 ? currentStepIndex + 1 : 1;

  return (
    <>
      <span className="stage-progress-label">
        <span>{label}</span>
        {showDynamicLabel ? <small>{progression.dynamic_stage_label}</small> : null}
      </span>
      {showProgress ? (
        <>
          <span
            className="stage-progress-inline"
            aria-label={`${percent}% progresjon, steg ${currentStepNumber} av ${steps.length}`}
          >
            <span className="stage-progress-track" aria-hidden="true">
              {steps.map((step, index) => (
                <i
                  key={getStepId(step) || `step-${index}`}
                  className={`stage-progress-step ${getStepStatusClassName(step)}`}
                />
              ))}
            </span>
            <small className="stage-progress-percent">{percent}%</small>
          </span>
          <span className="stage-progress-compact" aria-label={`Steg ${currentStepNumber} av ${steps.length}`}>
            {currentStepNumber}/{steps.length}
          </span>
        </>
      ) : null}
    </>
  );
};

const StageProgressPill = ({ label, progression, className = '' }) => (
  <span className={getStageProgressPillClassName(className, null, progression)}>
    <StageProgressPillContent label={label} progression={progression} />
  </span>
);

export default StageProgressPill;
